<template>
    <gridComponent :loader="loader" :items="items" :itemsData="itemsData" :headers="headers"
        :ColonneWidth="['10%', '15%', '15%', '15%', '5%', '15%', '15%']" :GetFunction="getOrdersList">
        <!-- Filters -->
        <template v-slot:[SlotsFilters.Filter0]>
            <ColumnFilter :title="$t('orders.orderNumber')" :Filter="OrderNumber" @cancel="OrderNumber = null"
                :getList="getOrdersList">
                <template v-slot:input>
                    <v-text-field v-model="OrderNumber" id="OrderNumber" variant="outlined" maxlength="33"
                        hide-details></v-text-field>
                </template>
            </ColumnFilter>
        </template>

        <template v-slot:[SlotsFilters.Filter1]>
            <ColumnFilter :title="$t('orders.clientName')" :Filter="ClientName" @cancel="ClientName = null"
                :getList="getOrdersList">
                <template v-slot:input>
                    <v-text-field v-model="ClientName" variant="outlined" maxlength="33" hide-details></v-text-field>
                </template>
            </ColumnFilter>
        </template>

        <template v-slot:[SlotsFilters.Filter2]>
            <ColumnFilter :title="$t('orders.creationDate')" :Filter="CreationDate" @cancel="CreationDate = null"
                :getList="getOrdersList">
                <template v-slot:input>
                    <span lang="en-CA" xml:lang="en-CA">
                        <dateField v-model="CreationDate" :min-date="new Date('2000-01-01')" :max-date="new Date()" />
                    </span>
                </template>
            </ColumnFilter>
        </template>

        <template v-slot:[SlotsFilters.Filter3]>
            <ColumnFilter :title="$t('orders.totalPrice')" :Filter="TotalAmount" @cancel="TotalAmount = null"
                :getList="getOrdersList">
                <template v-slot:input>
                    <v-text-field v-model="TotalAmount" variant="outlined" type="number" hide-details></v-text-field>
                </template>
            </ColumnFilter>
        </template>

        <template v-slot:[SlotsFilters.Filter4]>
            <ColumnFilter :title="$t('orders.status')" :Filter="Status" @cancel="Status = null"
                :getList="getOrdersList">
                <template v-slot:input>
                    <v-radio-group v-model="Status" column>
                        <v-radio :label="$t('orders.statusList.pending')" :value="0"></v-radio>
                        <v-radio :label="$t('orders.statusList.confirmed')" :value="1"></v-radio>
                        <v-radio :label="$t('orders.statusList.inProgress')" :value="2"></v-radio>
                        <v-radio :label="$t('orders.statusList.inDelivery')" :value="3"></v-radio>
                        <v-radio :label="$t('orders.statusList.delivered')" :value="4"></v-radio>
                        <v-radio :label="$t('orders.statusList.rejected')" :value="5"></v-radio>
                    </v-radio-group>
                </template>
            </ColumnFilter>
        </template>

        <template v-slot:[SlotsFilters.Filter5]>
            <ColumnFilter :title="$t('orders.endCollectionTime') + ' 🕒'" :Filter="CollectionTime"
                @cancel="resetDuration" :getList="getOrdersList">
                <template v-slot:input>
                    <span lang="en-CA" xml:lang="en-CA">
                        <DurationPicker v-model="durationModel" @durationChange="onDurationChange" :debug="false" />
                    </span>
                </template>
            </ColumnFilter>
        </template>

        <template v-slot:[SlotsFilters.Filter6]>
            <ColumnFilter :title="$t('orders.Type')" :Filter="Type" @cancel="Type = null" :getList="getOrdersList">
                <template v-slot:input>
                    <v-radio-group v-model="Type" column>
                        <v-radio :label="$t('orders.Scheduled')" :value="0"></v-radio>
                        <v-radio :label="$t('orders.Instant')" :value="1"></v-radio>
                    </v-radio-group>
                </template>
            </ColumnFilter>
        </template>

        <!-- Data Rows -->
        <template v-slot:[SlotsItems.item0]="{ item }">{{ item.numCommand }}</template>
        <template v-slot:[SlotsItems.item1]="{ item }">{{ item.client?.fullName || "N/A" }}</template>
        <template v-slot:[SlotsItems.item2]="{ item }">{{ formatDate(item.createdDate) }}</template>
        <template v-slot:[SlotsItems.item3]="{ item }">
            <span class="line" v-if="item.commandTotalPriceTTC < item.commandBaseTotalPriceTTC">
                {{ formatPrice(item.commandBaseTotalPriceTTC) }}
            </span>
            {{ formatPrice(item.commandTotalPriceTTC) }}

        </template>
        <template v-slot:[SlotsItems.item4]="{ item }">
            <StatusButton v-if="item" :title="getStatusText(item.status)" :color="getStatusColor(item.status)" />
        </template>
        <template v-slot:[SlotsItems.item5]="{ item }">
            <CountdownTimer v-if="item.endCollectionTime" :endTime="item.endCollectionTime"
                :maxTime="item.maxEndCollectionTime" />
            <span v-else>{{ $t('orders.NotCollectingYet') }}</span>
        </template>

        <template v-slot:[SlotsItems.item6]="{ item }">
            <span v-if="item.instantOrder">
                {{
                    $t('orders.Instant')
                }}
            </span>
            <span v-else>
                {{
                    $t('orders.Scheduled')
                }}
            </span>
        </template>

        <!-- Actions -->
        <template v-slot:edit="{ item, index }">
            <order-item-menu :Id="index" :orderId="item.idCommand" :getOrdersAfterDeleted="getOrdersList"
                @viewDetails="navigateToOrderDetails(item.idCommand)" />
        </template>
    </gridComponent>
</template>

<script>
import DurationPicker from '@/genericComponent/durationPicker.vue';
import gridComponent from "@/genericComponent/gridComponent.vue";
import ColumnFilter from "@/genericComponent/ColumnFilter.vue";
import dateField from "@/genericComponent/date-field.vue";
import StatusButton from "@/genericComponent/StatusButton.vue";
import OrderItemMenu from "./OrderItemMenu.vue";
import { formatPrice, getOrders } from "@/api/index";
import CountdownTimer from "@/genericComponent/countdownTimer.vue";

export default {
    components: {
        gridComponent,
        ColumnFilter,
        dateField,
        StatusButton,
        OrderItemMenu,
        CountdownTimer,
        DurationPicker
    },
    data() {
        return {
            durationModel: {
                years: 0,
                months: 0,
                days: 0,
                hours: 0,
                minutes: 0
            },
            loader: true,
            page: 1,
            pageSize: 10,
            items: [],
            itemsData: {},
            search: null,
            OrderNumber: null,
            ClientName: null,
            TotalQuantity: null,
            Status: null,
            Type: null,
            TotalAmount: null,
            CreationDate: null,
            CollectionTime: null,
            SortedBy: null,
            SortingType: null,
        };
    },
    created() {
        this.getOrdersList();
    },
    mounted() {
        this.emitter.off("event-test");
        this.emitter.emit("remise-search-at-null");
        this.emitter.on("event-test", (event) => {
            this.search = event;
            this.getOrdersList();
        });
    },
    unmounted() {
        this.emitter.off("event-test");
        this.emitter.emit("remise-search-at-null");
    },
    methods: {
        formatPrice(price) {
            return formatPrice(price)
        },
        resetDuration() {
            this.CollectionTime = null;
            this.durationModel = {
                years: 0,
                months: 0,
                days: 0,
                hours: 0,
                minutes: 0
            };
        },
        onDurationChange(durationData) {
            this.CollectionTime = {
                value: durationData.formattedDuration,
                dateTimeUtc: durationData.dateTimeUtc,
                dateTimeTicks: durationData.dateTimeTicks,
                duration: {
                    years: durationData.years,
                    months: durationData.months,
                    days: durationData.days,
                    hours: durationData.hours,
                    minutes: durationData.minutes
                }
            };
        },
        onCancelEndCollectionTime() {
            this.CollectionTime = null;
            this.durationModel = {
                years: 0,
                months: 0,
                days: 0,
                hours: 0,
                minutes: 0
            };
        },
        getOrdersList(page) {
            if (page) {
                this.page = page;
            }
            getOrders(
                this.page,
                this.pageSize,
                this.SortedBy,
                this.SortingType,
                this.Status,
                this.CreationDate,
                this.OrderNumber,
                this.ClientName,
                this.TotalAmount,
                this.TotalQuantity,
                this.CollectionTime,
                this.Type
            ).then((response) => {
                this.items = response.data.items;
                this.itemsData.numberOfPage = response.data.totalPages;
                this.itemsData.totalItemsCount = response.data.totalCount;
                this.itemsData.pageNumber = response.data.pageNumber;
                this.itemsData.hasPreviousPage = response.data.hasPreviousPage;
                this.itemsData.hasNextPage = response.data.hasNextPage;
                this.loader = false;
                this.items.forEach((order) => {
                    order.totalQuantity = this.getTotalQuantity(order.products);
                });
            });
        },
        onSearch(searchTerm) {
            this.search = searchTerm;
            this.getOrdersList();
        },
        getStatusText(status) {
            switch (status) {
                case 0:
                    return this.$t("orders.statusList.pending");
                case 1:
                    return this.$t("orders.statusList.confirmed");
                case 2:
                    return this.$t("orders.statusList.inProgress");
                case 3:
                    return this.$t("orders.statusList.inDelivery");
                case 4:
                    return this.$t("orders.statusList.delivered");
                case 5:
                    return this.$t("orders.statusList.rejected");
                case 6:
                    return this.$t("orders.statusList.canceled");
                default:
                    return this.$t("orders.statusList.unknown");
            }
        },
        getStatusColor(status) {
            switch (status) {
                case 0:
                    return "#C5CDD4"; // Gray for 'Pending'
                case 1:
                    return "#114EF7"; // Blue for 'Confirmed'
                case 2:
                    return "#FF8934"; // Orange for 'In Progress'
                case 3:
                    return "#3BBAFF"; // Light Blue for 'In Delivery'
                case 4:
                    return "#00CBA0"; // Green for 'Delivered'
                case 5:
                    return "#F25C5C"; // Red for 'Rejected'
                case 6:
                    return "#F25C5C"; // Red for 'Rejected'
                default:
                    return "#C5CDD4"; // Default Gray
            }
        },
        formatDate(dateString) {
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const utcDateString = dateString.replace(' ', 'T') + 'Z';
            const utcDate = new Date(utcDateString);

            const localDate = new Date(utcDate.toLocaleString('en-US', { timeZone: userTimeZone }));

            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, '0');
            const day = String(localDate.getDate()).padStart(2, '0');
            const hours = String(localDate.getHours()).padStart(2, '0');
            const minutes = String(localDate.getMinutes()).padStart(2, '0');

            return `${day}-${month}-${year} ${hours}:${minutes}`;
        },
        getTotalQuantity(products) {
            if (!products || !products.length) return 0;
            return products.reduce(
                (sum, product) => sum + (product.quantity || 0),
                0
            );
        },
        navigateToOrderDetails(orderId) {
            this.$router.push({
                name: "OrderDetails",
                params: { id: orderId },
            });
        },
    },
    computed: {
        headers() {
            return [
                this.$t("orders.orderNumber"),
                this.$t("orders.clientName"),
                this.$t("orders.creationDate"),
                this.$t("orders.totalAmount"),
                this.$t("orders.status"),
                this.$t("orders.endCollectionTime"),
                this.$t("orders.Type"),
            ];
        },
        SlotsItems() {
            const items = {
                item0: "item",
                item1: "item",
                item2: "item",
                item3: "item",
                item4: "item",
                item5: "item",
                item6: "item",
            };
            let i = 0;
            for (const key in items) {
                items[key] = "item" + i;
                i = i + 1;
            }
            return items;
        },
        SlotsFilters() {
            const filters = {
                Filter0: "Filter",
                Filter1: "Filter",
                Filter2: "Filter",
                Filter3: "Filter",
                Filter4: "Filter",
                Filter5: "Filter",
                Filter6: "Filter",
            };
            let i = 0;
            for (const key in filters) {
                filters[key] = "Filter" + i;
                i = i + 1;
            }
            return filters;
        },
    },
    watch: {
        search() {
            this.getOrdersList();
        },
    },
};
</script>

<style scoped>
.v-card-title {
    color: #001e50;
    font-size: 18px;
    line-height: 25.5px;
    font-weight: 700;
    font-family: inter;
}

.text-basil {
    color: #114ef7 !important;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    font-family: inter;
}

.slider {
    font-family: "Inter";
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    flex: content;
    border-bottom: "2px solid black";
}

.disabled {
    opacity: 0.5;
    pointer-events: none;
}
</style>
